'use client';

/** WHERE CLOSING A PAGE OVERLAY GOES.
 *
 *  `/settings` and `/account` are pages presented as overlays, so closing one means leaving the address
 *  rather than dismissing a dialog: the reader goes back to the surface they opened it from. That is a
 *  history step, and history is exactly what a cold load does not have — open `/settings` in a fresh tab
 *  and `history.back()` either does nothing at all or hands the reader to whatever was in the tab before
 *  the app. The first leaves the close control looking broken; the second leaves the app entirely.
 *
 *  So the close asks the browser whether the entry behind the current one belongs to this app. The
 *  Navigation API answers exactly that: `navigation.canGoBack` is true when the tab's history holds an
 *  entry of this origin behind the current one, and entries of other sites are never part of its list.
 *
 *  It is not a count of `history.length`, which stops at the browser's session-history cap (50 entries in
 *  Chromium and Firefox): in a long-lived tab every later push left the length unchanged, so an overlay
 *  opened from Chat or any other page closed to the dashboard instead of back to that page.
 *
 *  A browser without the Navigation API (Safari before 26.2, Firefox before 147) cannot tell, and gets the
 *  cold-load answer: the close lands on a real page of the app rather than possibly leaving it. */
export function hasAppHistoryBehind(): boolean {
  return 'navigation' in window && window.navigation.canGoBack;
}

/** Where a page overlay closes to when there is nothing behind it. The app's own landing route rather
 *  than `/`, which only redirects here and would cost the reader a second navigation to watch. */
export const PAGE_OVERLAY_FALLBACK_ROUTE = '/dash';
